import { getV1CompaniesCompanyIdIamRoles } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import useLatestRequestSignal from '~hooks/useLatestRequestSignal/useLatestRequestSignal';
import { RoleItem } from '~hooks/useUsers/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

async function fetchRoles(companyId: string, signal?: AbortSignal) {
  const response = await getV1CompaniesCompanyIdIamRoles({
    path: { 'company-id': companyId },
    signal,
  });

  return response.data.data.map((item) => RoleItem.parse(item));
}

export const useUserRoles = () => {
  const { userStore } = useStores();
  const effectiveCompanyId = getEffectiveUserCompanyId(userStore);
  const issueRequestSignal = useLatestRequestSignal();
  const issueCompanyRolesSignal = useLatestRequestSignal();

  const getUserRoles = useCallback(async () => {
    const signal = issueRequestSignal();

    try {
      const userRoles = await fetchRoles(effectiveCompanyId, signal);
      if (signal.aborted) {
        return;
      }

      userStore.setUserRoles(userRoles);
    } catch (error) {
      if (signal.aborted) {
        return;
      }

      connection.handleRequestError(
        error,
        t('error_messages.user_roles.failed_to_fetch'),
      );
    }
  }, [effectiveCompanyId, issueRequestSignal, userStore]);

  const getCompanyUserRoles = useCallback(
    async (companyId: string) => {
      const signal = issueCompanyRolesSignal();

      try {
        const roles = await fetchRoles(companyId, signal);

        return signal.aborted ? undefined : roles;
      } catch (error) {
        if (signal.aborted) {
          return undefined;
        }

        connection.handleRequestError(
          error,
          t('error_messages.user_roles.failed_to_fetch'),
        );
      }
    },
    [issueCompanyRolesSignal],
  );

  return { getUserRoles, getCompanyUserRoles };
};
